import { formatPerformedValue } from "./loadDisplay";
import { formatReviewDuration } from "./executionUi";

// Read-only presentation of the saved-workout journal (blueprint §15.10) from
// the `GET /api/workout-executions/:id` read model. Formatting only: every
// value is shown as it was entered (§13.4), nothing is recomputed or converted.
// A set's `setType` is not presented: the Workout Log has no set-type concept.

const SHORT_WEEKDAYS = Object.freeze(["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]);
const SHORT_MONTHS = Object.freeze([
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]);

export function trainAgainPath(executionId) {
  const params = new URLSearchParams({ kind: "TRAIN_AGAIN", executionId });
  return `/train/start?${params}`;
}

export function workoutDetailsPath(executionId) {
  return `/train/log/${encodeURIComponent(executionId)}`;
}

// A stored calendar date, never reinterpreted in another timezone (I-21).
export function formatTrainingDate(dateKey) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ""));
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return `${SHORT_WEEKDAYS[date.getUTCDay()]}, ${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${match[1]}`;
}

function pluralize(count, singular) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

export function formatWorkoutStats(execution) {
  const seconds = Number(execution?.activeDurationSeconds);
  const sets = Number(execution?.performedSetCount);
  return [
    Number.isFinite(seconds) && seconds >= 60 ? formatReviewDuration(seconds).label : null,
    Number.isFinite(sets) && sets > 0 ? pluralize(sets, "set") : null,
  ].filter(Boolean);
}

function setLine(item, set) {
  return {
    key: set.setId || `${set.setIndex}`,
    text: formatPerformedValue({
      measurementType: item.measurementType,
      load: set.load,
      reps: set.reps,
      seconds: set.seconds,
      rir: set.rir,
    }),
  };
}

// The workout reflection written in Review ("Your thoughts about this
// workout"), or null. One note about this workout, never an exercise note.
export function workoutReflection(details) {
  const notes = details?.execution?.notes;
  return typeof notes === "string" && notes.trim() ? notes : null;
}

// One journal entry per execution item, in execution order.
export function buildWorkoutJournal(details) {
  return (details?.items || []).map((item, index) => {
    const cardio = item.measurementType === "CARDIO_MINUTES";
    const cardioDone = cardio && item.cardio?.completed && Number(item.cardio.minutes) >= 1;
    const unperformed = Number(item.notPerformedPrescribedCount) || 0;
    return {
      key: `${index}`,
      name: item.exerciseName,
      substitutedFrom: item.substitutedFrom || null,
      // "Treadmill Walk · 20 min"
      cardioValue: cardioDone
        ? formatPerformedValue({ measurementType: item.measurementType, cardioMinutes: item.cardio.minutes })
        : null,
      sets: cardio ? [] : (item.sets || []).map((set) => setLine(item, set)),
      notPerformedLine: cardio
        ? (cardioDone ? null : "Not performed")
        : unperformed > 0
          ? `${pluralize(unperformed, "prescribed set")} not performed`
          : null,
      notes: item.notes ? String(item.notes) : null,
    };
  });
}
